"use client";

import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  ColorContext,
  theme,
} from "@/components/canvas-editor/components/Container";
import { cn } from "@/lib/utils";

const toggleVariants = cva(
  `
    relative
    inline-flex items-center justify-center rounded-md 
    text-sm font-medium 
    text-slate-dim hover:text-slate-normal
    ring-offset-background transition-colors 
    focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 
    disabled:pointer-events-none disabled:opacity-50 
    data-[state=on]:bg-slate-5 dark:data-[state=on]:bg-slatedark-5 data-[state=on]:text-slate-normal
    aria-pressed:bg-slate-5 dark:aria-pressed:bg-slatedark-5 aria-pressed:text-slate-normal
    [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 gap-2
  `,
  {
    variants: {
      variant: {
        default: "bg-slate-ghost",
        outline: "border border-slate-normal ",
      },
      size: {
        default: "h-10 w-10",
        xs: "h-7 w-7",
        sm: "h-8 w-8",
        lg: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ToggleProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>;

const Toggle = ({ className, variant, size, ...props }: ToggleProps) => {
  const color = React.use(ColorContext);

  return (
    <TogglePrimitive.Root
      className={cn(
        toggleVariants({ variant, size, className }),
        color && theme.button[color],
      )}
      {...props}
    />
  );
};

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle };
